import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  ShieldCheckIcon,
  EyeIcon,
  PlusIcon,
  PencilIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";
import { chemicalInventoryApi } from "../../../../../api/chemical-inventory";
import {
  getHseSafetyInspections,
  exportHseSafetyInspectionPdf,
} from "../../../../../api/hse/safetyInspections";
import { ResourceLoading } from "../../../../../components/utils/ResourceLoading";
import { ResourceEmpty } from "../../../../../components/utils/ResourceEmpty";
import { HsePageChrome, useHseBusyAction } from "../../../../../components/dashboard/hse/HsePageChrome";
import { ModuleFilter } from "../../../../../components/utils/ModuleFilter";
import { PMDialog } from "../../../../../components/utils/PMDialog";
import { HseSafetyInspectionForm } from "../../../../../components/dashboard/hse/hseSafetyInspectionForm";
import { HseRecordActionsMenu } from "../../../../../components/dashboard/hse/HseRecordActionsMenu";
import { RiskSubnav } from "../../../../../components/dashboard/hse/risk/RiskSubnav";
import { HseListCard, HsePageBody } from "../../../../../components/dashboard/hse/HsePageBody";
import { HseGuideButton, HseModuleGuide } from "../../../../../components/dashboard/hse/HseModuleGuide";

function canEditInspection(inspection) {
  if (!inspection) return false;
  if (typeof inspection.can_be_edited === "boolean") return inspection.can_be_edited;
  return ["draft", "in_progress"].includes(inspection.status);
}

/**
 * HSE/KS Protective Equipment List - Vernerunder (ISO 45001 kap. 8.1.2)
 * Shows all protective equipment from chemical inventory and safety inspections
 */
export const HseKsProtectiveEquipmentList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inspectionPath = (id) => `/dashboard/hse-ks/risk/protective-equipment/${id}`;
  const [equipmentList, setEquipmentList] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [guideOpen, setGuideOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [filters, setFilters] = useState({
    status: "",
    start_date: "",
    end_date: "",
    search: "",
  });
  const { run, isBusy } = useHseBusyAction();
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    current_count: 0,
    total_pages: 0,
  });

  useEffect(() => {
    fetchEquipment();
    fetchInspections();
  }, [searchQuery, filters, pagination.page, pagination.per_page]);

  const fetchEquipment = async () => {
    try {
      const response = await chemicalInventoryApi.getChemicalInventories({
        search: searchQuery,
        per_page: 100,
      });
      
      // Extract protective equipment from all chemicals
      const allEquipment = [];
      if (response.data) {
        response.data.forEach((chemical) => {
          if (chemical.protective_equipment && Array.isArray(chemical.protective_equipment)) {
            chemical.protective_equipment.forEach((equipment) => {
              // Avoid duplicates by checking if equipment already exists
              const exists = allEquipment.find(
                (eq) => eq.name === equipment.name && eq.type === equipment.type
              );
              if (!exists) {
                allEquipment.push({
                  ...equipment,
                  chemical_id: chemical.id,
                  chemical_name: chemical.name,
                  cas_number: chemical.cas_number,
                });
              }
            });
          }
        });
      }
      
      setEquipmentList(allEquipment);
    } catch (err) {
      console.error("Error fetching protective equipment:", err);
    }
  };

  const fetchInspections = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        page: pagination.page,
        per_page: pagination.per_page,
      };
      const response = await getHseSafetyInspections(params);
      setInspections(response.data.data || []);
      setPagination((prev) => ({
        ...prev,
        total: response.data.meta?.total || 0,
        current_count: response.data.meta?.current_count || 0,
        total_pages: response.data.meta?.total_pages || 0,
      }));
    } catch (err) {
      console.error("Error fetching inspections:", err);
      setError(true);
      toast.error(t("Kunne ikke laste vernerunder"));
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = (inspection) => {
    setInspections((prev) => {
      const existingIndex = prev.findIndex((i) => i.id === inspection.id);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = inspection;
        return updated;
      } else {
        return [inspection, ...prev];
      }
    });
    setShowForm(false);
    setSelectedInspection(null);
    fetchInspections();
  };

  const handleEdit = (inspection) => {
    setSelectedInspection(inspection);
    setShowForm(true);
  };

  const handleExportPdf = (inspection) =>
    run(`pdf-${inspection.id}`, async () => {
      try {
        await exportHseSafetyInspectionPdf(
          inspection.id,
          `hse-vernerunde-${inspection.id}.pdf`
        );
        toast.success(t("hse.protective.export_pdf_success"));
      } catch (err) {
        toast.error(err.message || t("hse.protective.export_pdf_error"));
      }
    });

  return (
    <>
      <HsePageChrome
        variant="list"
        title={t("hse.protective.title")}
        subtitle={t("hse.protective.subtitle")}
        listCount={loading ? null : pagination.total}
        mainButton={{
          label: t("hse.protective.new"),
          icon: PlusIcon,
          action: () => {
            setSelectedInspection(null);
            setShowForm(true);
          },
        }}
      >
      <HsePageBody>
          <RiskSubnav />
          <div className="mb-4 flex justify-end">
            <HseGuideButton onClick={() => setGuideOpen(true)} />
          </div>
          <HseModuleGuide topic="protective" open={guideOpen} onClose={() => setGuideOpen(false)} />
          <ModuleFilter
            filterCard
            hideStatusFilter
            searchPlaceholder={t("hse.protective.search")}
            initialFilters={{
              query: searchQuery,
              inspection_statuses: filters.status ? [filters.status] : [],
            }}
            onFiltersChange={(next) => {
              setSearchQuery(next.query || "");
              setFilters((prev) => ({
                ...prev,
                search: next.query || "",
                status: next.inspection_statuses?.[0] || "",
              }));
            }}
            customDropdownFilters={[
              {
                key: "inspection_statuses",
                label: t("Status"),
                options: [
                  { value: "draft", label: t("hse.protective.status.draft"), class: "bg-slate-100 text-slate-800" },
                  { value: "in_progress", label: t("hse.protective.status.in_progress"), class: "bg-slate-100 text-slate-800" },
                  { value: "completed", label: t("hse.protective.status.completed"), class: "bg-slate-100 text-slate-800" },
                  { value: "closed", label: t("hse.protective.status.closed"), class: "bg-slate-100 text-slate-800" },
                ],
              },
            ]}
          />

          {/* Safety Inspections List */}
          <HseListCard>
            <div className="px-5 py-3 border-b border-slate-100">
              <h3 className="text-sm font-extrabold text-slate-900">{t("hse.protective.title")}</h3>
            </div>
            {loading ? (
              <div className="p-6">
                <ResourceLoading />
              </div>
            ) : !inspections || inspections.length === 0 ? (
              <div className="px-4 py-5 sm:p-6">
                <ResourceEmpty message={t("hse.protective.empty")} />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("hse.protective.col_title")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("hse.protective.col_date")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("hse.protective.col_location")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("hse.protective.col_status")}
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("hse.protective.actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {inspections.map((inspection) => (
                      <tr
                        key={inspection.id}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => navigate(inspectionPath(inspection.id))}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Link
                            to={inspectionPath(inspection.id)}
                            className="text-sm font-medium text-slate-900 hover:text-[#1463C7]"
                            onClick={(event) => event.stopPropagation()}
                          >
                            {inspection.title}
                          </Link>
                          {inspection.description ? (
                            <div className="text-sm text-gray-500">
                              {inspection.description}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {inspection.inspection_date
                            ? new Date(inspection.inspection_date).toLocaleDateString()
                            : "—"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {inspection.location || "—"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              inspection.status === "draft"
                                ? "bg-gray-100 text-gray-800"
                                : inspection.status === "in_progress"
                                ? "bg-blue-100 text-blue-800"
                                : inspection.status === "completed"
                                ? "bg-green-100 text-green-800"
                                : inspection.status === "closed"
                                ? "bg-gray-100 text-gray-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {t(`hse.protective.status.${inspection.status}`, {
                              defaultValue: inspection.status_display || inspection.status,
                            })}
                          </span>
                        </td>
                        <td
                          className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <HseRecordActionsMenu
                            label={t("hse.protective.actions")}
                            items={[
                              {
                                key: "open",
                                label: t("hse.protective.open"),
                                icon: EyeIcon,
                                onClick: () => navigate(inspectionPath(inspection.id)),
                              },
                              {
                                key: "edit",
                                label: t("hse.protective.edit"),
                                icon: PencilIcon,
                                hidden: !canEditInspection(inspection),
                                onClick: () => handleEdit(inspection),
                              },
                              {
                                key: "export_pdf",
                                label: t("hse.protective.export_pdf"),
                                icon: PrinterIcon,
                                disabled: isBusy(),
                                onClick: () => handleExportPdf(inspection),
                              },
                            ]}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </HseListCard>

          <HseListCard>
            <div className="border-b border-slate-100 px-5 py-3">
              <h3 className="text-sm font-extrabold text-slate-900">{t("hse.protective.equipment_title")}</h3>
            </div>
            {!equipmentList || equipmentList.length === 0 ? (
              <div className="px-6 py-16">
                <ResourceEmpty message={t("hse.protective.equipment_empty")} />
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {equipmentList.map((equipment, index) => (
                  <li key={`${equipment.chemical_id}-${index}`} className="px-6 py-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div className="flex-shrink-0">
                          <ShieldCheckIcon className="h-5 w-5 text-blue-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {equipment.name || equipment.type || t("Ukjent utstyr")}
                            </p>
                          </div>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span>{equipment.type && `${t("Type")}: ${equipment.type}`}</span>
                            <span>{equipment.chemical_name && `${t("Kjemikalium")}: ${equipment.chemical_name}`}</span>
                          </div>
                          {equipment.description && (
                            <p className="mt-1 text-sm text-gray-500">{equipment.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Link
                          to={`/dashboard/hse-ks/risk/chemical-inventory/${equipment.chemical_id}`}
                          className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <EyeIcon className="h-4 w-4 mr-1" />
                          {t("Vis kjemikalium")}
                        </Link>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </HseListCard>
      </HsePageBody>
      </HsePageChrome>

      {/* Form Dialog */}
      {showForm && (
        <PMDialog
          open={showForm}
          onClose={() => {
            setShowForm(false);
            setSelectedInspection(null);
          }}
          title={
            selectedInspection ? t("hse.protective.edit") : t("hse.protective.new")
          }
          size="7xl"
        >
          <HseSafetyInspectionForm
            inspection={selectedInspection}
            onSubmit={handleFormSubmit}
            onCancel={() => {
              setShowForm(false);
              setSelectedInspection(null);
            }}
          />
        </PMDialog>
      )}
    </>
  );
};

